import CustomTooltip from '../../../../shared/CustomTooltip';
import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Card,
    Checkbox,
    Collapse,
    FormControlLabel,
    FormHelperText,
    Grid,
    InputAdornment,
    InputLabel,
    List,
    ListItem,
    Stack,
    Switch,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import FormAutocomplete from '../../../../shared/FormAutocomplete';
import {
    MaxHoursOfHire,
    MaxHoursOfHireCommercial,
    MaxHoursOfHireProperty,
    MinHoursOfHire,
    MinHoursOfHireCommercial,
    MinHoursOfHirePropertyCorp,
    MinHoursOfHirePropertyPersonal,
} from '../../Owner/HoursOfHire';
import FormTextField from '../../../../shared/FormTextField';
import FormCheckbox from '../../../../shared/FormCheckbox';
import { AccessTime, CalendarMonth, HelpOutline } from '@mui/icons-material';
import FormSelect from '../../../../shared/FormSelect';
import { driversAgeArray } from '../../Owner/Boat/NumberArray';
import { TimeArray } from '../../Owner/Property/TimeArray';
import { useTheme } from '@mui/material/styles';
import FormMultiAutocomplete from '../../../../shared/FormMultiAutocomplete';
import { useGetAllGuestsQuery } from '../../../../../app/Slices/SevenDayApiSlice';
import { ExperienceType } from '../../../ExperienceReviews/ReviewWizard';
import { convertTimeToMinutes } from '../../../../../shared/utils';
import { useFormState } from '../useFormState';

/**
 * Stores the inverse value of 'requiresBookingApproval'
 */
const BookingApprovalCheckbox = () => {
    const { control } = useFormContext();
    return (
        <Controller
            name='requiresBookingApproval'
            control={control}
            render={({ field: { value, onChange, ref } }) => (
                <Checkbox
                    id='requires-booking-approval__input'
                    inputRef={ref}
                    checked={!value}
                    onChange={(_, checked) => {
                        onChange(!checked);
                    }}
                    sx={{ color: 'text.primary', p: 0 }}
                />
            )}
        />
    );
};

const ExperienceAvailability = () => {
    const { experience } = useFormState();

    const type = experience.type;
    const visitType = experience.visitType;

    const { data: guests } = useGetAllGuestsQuery(`/guests`);

    const {
        control,
        setValue,
        formState: { errors },
    } = useFormContext();
    const theme = useTheme();

    const isIpad = useMediaQuery('(max-width: 1200px)');
    const isMobile = useMediaQuery('(max-width: 770px)');

    const requiresBookingApproval = useWatch({
        control,
        name: 'requiresBookingApproval',
    });
    const autoApprovalGuests = useWatch({
        control,
        name: 'autoApprovalGuests',
    });

    const [autoApprovalAppliesToAll, setAutoApprovalAppliesToAll] =
        useState(true);

    const days = [
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
        'sunday',
    ];

    const minHireArray =
        type === 'property' && visitType === 'business'
            ? MinHoursOfHirePropertyCorp
            : type === 'property' && visitType === 'personal'
              ? MinHoursOfHirePropertyPersonal
              : type === 'commercial'
                ? MinHoursOfHireCommercial
                : MinHoursOfHire;
    const maxHireArray =
        type === 'property'
            ? MaxHoursOfHireProperty
            : type === 'commercial'
              ? MaxHoursOfHireCommercial
              : MaxHoursOfHire;

    const handleAutoApprovalAppliesToAll: (
        event: React.SyntheticEvent<Element, Event>,
        checked: boolean,
    ) => void = (_, checked) => {
        if (checked) setValue('autoApprovalGuests', [], { shouldDirty: true });
        setAutoApprovalAppliesToAll(checked);
    };

    useEffect(() => {
        if (autoApprovalGuests.length) setAutoApprovalAppliesToAll(false);
    }, [autoApprovalGuests.length]);

    const minHireHours = useWatch({ control, name: 'minHireHours' });
    const halfDayCheckoutTime = useWatch({
        control,
        name: 'halfDayCheckoutTime',
    });
    const halfDayCheckinTime = useWatch({
        control,
        name: 'halfDayCheckinTime',
    });

    const showHalfDayBookingTimes =
        [ExperienceType.Property, ExperienceType.Commercial].includes(type) &&
        minHireHours < 24;

    const minimumGroupTwoCheckinMinutes = useMemo(() => {
        if (!halfDayCheckoutTime) return null;
        return convertTimeToMinutes(halfDayCheckoutTime) + 60;
    }, [halfDayCheckoutTime]);

    const halfDayGroupTwoCheckinOptions = useMemo(
        () =>
            TimeArray.filter(({ value }) => {
                if (minimumGroupTwoCheckinMinutes === null) return true;
                return (
                    convertTimeToMinutes(value) >= minimumGroupTwoCheckinMinutes
                );
            }),
        [minimumGroupTwoCheckinMinutes],
    );

    const handleHalfDayCheckoutChange = (
        selectedTime: { value: string; label: string } | null,
    ) => {
        if (!selectedTime || !halfDayCheckinTime) return;

        const checkoutMinutes = convertTimeToMinutes(selectedTime.value);
        const checkinMinutes = convertTimeToMinutes(halfDayCheckinTime);
        setValue('bufferHours', (checkinMinutes - checkoutMinutes) / 60);
    };

    const handleHalfDayCheckinChange = (
        selectedTime: { value: string; label: string } | null,
    ) => {
        if (!selectedTime || !halfDayCheckoutTime) return;

        const checkinMinutes = convertTimeToMinutes(selectedTime.value);
        const checkoutMinutes = convertTimeToMinutes(halfDayCheckoutTime);
        setValue('bufferHours', (checkinMinutes - checkoutMinutes) / 60);
    };

    const getTimeValueFromMinutes = (minutes: number) => {
        return TimeArray.find(
            ({ value }) => convertTimeToMinutes(value) === minutes,
        )?.value;
    };

    const handleBufferHoursChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const typedBufferHours = Number(event.target.value);
        if (Number.isNaN(typedBufferHours) || typedBufferHours < 0) return;

        const roundedToHalfHour = Math.round(typedBufferHours * 2) / 2;
        const isHalfHourIncrement = Number.isInteger(typedBufferHours * 2);
        const nextBufferHours = isHalfHourIncrement
            ? typedBufferHours
            : roundedToHalfHour;

        if (!isHalfHourIncrement) {
            setValue('bufferHours', nextBufferHours);
        }

        const bufferMinutes = nextBufferHours * 60;

        if (halfDayCheckoutTime) {
            const checkoutMinutes = convertTimeToMinutes(halfDayCheckoutTime);
            const computedCheckinTime = getTimeValueFromMinutes(
                checkoutMinutes + bufferMinutes,
            );
            if (computedCheckinTime) {
                setValue('halfDayCheckinTime', computedCheckinTime);
            }
            return;
        }
    };

    const HalfDayBookingTooltip = () => {
        return (
            <CustomTooltip
                slotProps={{
                    tooltip: {
                        sx: {
                            p: 1,
                            maxHeight: 400,
                            overflowY: 'auto',
                        },
                    },
                }}
                title={
                    <Typography>
                        <List
                            disablePadding
                            sx={{
                                fontSize: '14px',
                                py: 0,
                                '& .MuiListItem-root': {
                                    px: 1.3,
                                    py: 0.5,
                                },
                            }}
                        >
                            <ListItem>
                                When enabling Half-Day Bookings, guests can book
                                either the morning or afternoon session of your
                                space.
                            </ListItem>
                            <ListItem>
                                To allow enough time for cleaning, resetting the
                                room, catering setup, or preparation between
                                bookings, a buffer period is automatically
                                created between the two sessions.
                            </ListItem>

                            <ListItem
                                sx={{
                                    fontWeight: 'bold',
                                }}
                            >
                                For example:
                            </ListItem>
                            <ListItem>
                                • Morning Booking: 8:00am – 12:00pm
                            </ListItem>
                            <ListItem>
                                • Buffer Period: 12:00pm – 2:00pm
                            </ListItem>
                            <ListItem>
                                • Afternoon Booking: 2:00pm – 6:00pm
                            </ListItem>
                            <ListItem>
                                As the host, you'll need to enter:
                            </ListItem>

                            <ListItem>
                                <Typography
                                    component='span'
                                    sx={{
                                        fontSize: '14px',
                                    }}
                                >
                                    <Box
                                        style={{
                                            fontWeight: 'bold',
                                        }}
                                        component='span'
                                    >
                                        • Group 1 Check-Out Time
                                    </Box>{' '}
                                    – the time the morning guest must vacate the
                                    space.
                                </Typography>
                            </ListItem>
                            <ListItem>
                                <Typography
                                    component='span'
                                    sx={{
                                        fontSize: '14px',
                                    }}
                                >
                                    <Box
                                        style={{
                                            fontWeight: 'bold',
                                        }}
                                        component='span'
                                    >
                                        • Group 2 Check-In Time
                                    </Box>{' '}
                                    – the earliest time the next guest can
                                    access the space.
                                </Typography>
                            </ListItem>
                            <ListItem>
                                The time between these two settings becomes the
                                buffer period and cannot be booked by guests.
                            </ListItem>
                            <ListItem>
                                This ensures there is adequate time to prepare
                                the space between bookings and helps provide a
                                seamless experience for both guests.
                            </ListItem>
                            {type === ExperienceType.Property && (
                                <ListItem>
                                    <Typography
                                        component='span'
                                        sx={{
                                            fontSize: '14px',
                                        }}
                                    >
                                        <Box
                                            style={{
                                                fontWeight: 'bold',
                                            }}
                                            component='span'
                                        >
                                            Note:
                                        </Box>{' '}
                                        If the guests checkout before the
                                        half-day checkout time and the hourly
                                        charge calculates to be less than a full
                                        day charge, the hourly charge will
                                        apply. You need to configure the
                                        flexible pricing for this feature to
                                        work.
                                    </Typography>
                                </ListItem>
                            )}
                        </List>
                    </Typography>
                }
                placement='right'
            >
                <HelpOutline sx={{ fontSize: '16px' }} />
            </CustomTooltip>
        );
    };

    return (
        <fieldset>
            <Stack spacing={2}>
                <div>
                    <Typography variant='h2' component='legend'>
                        Availability
                    </Typography>
                    <Typography color='text.secondary' gutterBottom>
                        Specify available days and times for your experience
                    </Typography>
                </div>
                <div>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <InputLabel htmlFor='min-hire-hours__input'>
                                Minimum Hire Period
                            </InputLabel>
                            <FormAutocomplete
                                id='min-hire-hours__input'
                                name='minHireHours'
                                control={control}
                                options={minHireArray}
                                startAdornment={
                                    <InputAdornment position='end'>
                                        <CalendarMonth
                                            fontSize='small'
                                            color='disabled'
                                        />
                                    </InputAdornment>
                                }
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InputLabel htmlFor='max-hire-hours__input'>
                                Maximum Hire Period
                            </InputLabel>
                            <FormAutocomplete
                                id='max-hire-hours__input'
                                name='maxHireHours'
                                control={control}
                                options={maxHireArray}
                                startAdornment={
                                    <InputAdornment position='end'>
                                        <CalendarMonth
                                            fontSize='small'
                                            color='disabled'
                                        />
                                    </InputAdornment>
                                }
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InputLabel htmlFor='checkin-time__input'>
                                {type === 'vehicle'
                                    ? 'Pickup From'
                                    : type === 'commercial'
                                      ? 'Access From'
                                      : 'Checkin From'}
                            </InputLabel>
                            <FormAutocomplete
                                id='checkin-time__input'
                                name='checkinTime'
                                control={control}
                                options={TimeArray}
                                startAdornment={
                                    <InputAdornment position='end'>
                                        <AccessTime
                                            fontSize='small'
                                            color='disabled'
                                        />
                                    </InputAdornment>
                                }
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InputLabel htmlFor='checkout-time__input'>
                                {type === 'vehicle'
                                    ? 'Dropoff Before'
                                    : type === 'commercial'
                                      ? 'Access To'
                                      : 'Checkout Before'}
                            </InputLabel>
                            <FormAutocomplete
                                id='checkout-time__input'
                                name='checkoutTime'
                                control={control}
                                options={TimeArray}
                                startAdornment={
                                    <InputAdornment position='end'>
                                        <AccessTime
                                            fontSize='small'
                                            color='disabled'
                                        />
                                    </InputAdornment>
                                }
                            />
                        </Grid>
                    </Grid>
                </div>
                <Collapse
                    in={showHalfDayBookingTimes}
                    timeout='auto'
                    unmountOnExit
                >
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <InputLabel htmlFor='half-day-checkout-time__input'>
                                Half Day Checkout for Group 1{' '}
                                <HalfDayBookingTooltip />
                            </InputLabel>
                            <FormAutocomplete
                                id='half-day-checkout-time__input'
                                name='halfDayCheckoutTime'
                                control={control}
                                options={TimeArray}
                                onOptionSelect={handleHalfDayCheckoutChange}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InputLabel htmlFor='half-day-checkin-time__input'>
                                Half Day Checkin for Group 2{' '}
                                <HalfDayBookingTooltip />
                            </InputLabel>
                            <FormAutocomplete
                                id='half-day-checkin-time__input'
                                name='halfDayCheckinTime'
                                control={control}
                                options={halfDayGroupTwoCheckinOptions}
                                onOptionSelect={handleHalfDayCheckinChange}
                            />
                        </Grid>
                    </Grid>
                </Collapse>
                <div>
                    <InputLabel htmlFor='cleaning-buffer__input'>
                        Cleaning Buffer
                    </InputLabel>
                    <FormTextField
                        id='cleaning-buffer__input'
                        name='bufferHours'
                        control={control}
                        type='number'
                        placeholder='3'
                        inputProps={{ min: 0, step: 0.5 }}
                        onChange={handleBufferHoursChange}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position='end'>
                                    <Typography variant='body2'>
                                        hours
                                    </Typography>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <FormHelperText>
                        Amount of hours to clean the {type} before another
                        reservation can be made
                    </FormHelperText>
                </div>
                <Grid item xs />
                <div>
                    <InputLabel htmlFor='availability__input'>
                        Availability{' '}
                        <CustomTooltip
                            title={
                                <Typography>
                                    <List>
                                        <ListItem>
                                            Select the days in which the
                                            property is available to book.
                                        </ListItem>
                                        <ListItem>
                                            Unselected days will appear grey in
                                            the calendar and the guest will not
                                            be able to select these days
                                        </ListItem>
                                    </List>
                                </Typography>
                            }
                            placement='right'
                        >
                            <HelpOutline sx={{ fontSize: '16px' }} />
                        </CustomTooltip>
                    </InputLabel>
                    <Grid container spacing={1} justifyContent='flex-start'>
                        {days.map((day: any) => (
                            <Grid item xs key={day}>
                                <Controller
                                    key={day}
                                    name={day}
                                    control={control}
                                    render={({ field: { value, ref } }) =>
                                        isMobile ? (
                                            <Button
                                                variant={
                                                    value
                                                        ? 'contained'
                                                        : 'outlined'
                                                }
                                                onClick={() =>
                                                    setValue(day, !value, {
                                                        shouldDirty: true,
                                                    })
                                                }
                                                disableElevation
                                                disableRipple
                                                ref={ref}
                                                sx={{
                                                    flexGrow: 1,
                                                    minWidth: 0,
                                                    px: 2,
                                                    textAlign: 'center',
                                                }}
                                            >
                                                {isMobile || isIpad
                                                    ? day.charAt(0)
                                                    : day}
                                            </Button>
                                        ) : (
                                            <Card
                                                sx={{
                                                    height: '100px',
                                                    bgcolor: value
                                                        ? theme.palette.primary
                                                              .main
                                                        : theme.palette.neutral
                                                              .main,
                                                    color: value
                                                        ? theme.palette.primary
                                                              .contrastText
                                                        : theme.palette.text
                                                              .primary,
                                                }}
                                                onClick={() =>
                                                    setValue(day, !value, {
                                                        shouldDirty: true,
                                                    })
                                                }
                                                ref={ref}
                                            >
                                                <Box
                                                    display='flex'
                                                    flexDirection='column'
                                                    alignItems='center'
                                                    height='100%'
                                                    gap={1}
                                                    justifyContent='center'
                                                    textAlign='center'
                                                >
                                                    <Typography
                                                        sx={{
                                                            color: value
                                                                ? theme.palette
                                                                      .primary
                                                                      .contrastText
                                                                : theme.palette
                                                                      .text
                                                                      .primary,
                                                        }}
                                                    >
                                                        {day
                                                            .charAt(0)
                                                            .toUpperCase() +
                                                            day.slice(1)}
                                                    </Typography>
                                                </Box>
                                            </Card>
                                        )
                                    }
                                />
                            </Grid>
                        ))}
                    </Grid>
                    {errors.monday && (
                        <FormHelperText error>
                            {errors.monday.message as string}
                        </FormHelperText>
                    )}
                </div>
                <Grid container item xs={12} pb={2}>
                    {' '}
                    <Grid item xs={12} display='flex' gap={1}>
                        <FormCheckbox
                            id='public-holidays__input'
                            name='publicHolidays'
                            control={control}
                            sx={{ p: 0 }}
                        />
                        <InputLabel
                            htmlFor='public-holidays__input'
                            sx={{
                                mt: 1,
                            }}
                        >
                            Available on Public Holidays
                        </InputLabel>
                    </Grid>
                    <Grid item xs={12} display='flex' gap={1}>
                        <BookingApprovalCheckbox />
                        <InputLabel
                            htmlFor='requires-booking-approval__input'
                            sx={{
                                mt: 1,
                            }}
                        >
                            User Instant Book{' '}
                            <CustomTooltip
                                title={
                                    <Typography>
                                        <List>
                                            <ListItem>
                                                Turn on to automatically accept
                                                reservations. Turn off to
                                                manually accept or decline
                                                reservation requests.
                                            </ListItem>
                                        </List>
                                    </Typography>
                                }
                                placement='right'
                            >
                                <HelpOutline
                                    sx={{
                                        fontSize: '16px',
                                    }}
                                />
                            </CustomTooltip>
                        </InputLabel>
                    </Grid>
                    {!requiresBookingApproval && (
                        <Grid item xs={12} display='flex' gap={1}>
                            <Box>
                                <FormControlLabel
                                    label={
                                        <Typography
                                            sx={{
                                                color: 'text.primary',
                                            }}
                                        >
                                            Applies to All
                                        </Typography>
                                    }
                                    control={<Switch />}
                                    checked={autoApprovalAppliesToAll}
                                    onChange={handleAutoApprovalAppliesToAll}
                                    sx={{ mr: 1 }}
                                />
                                <CustomTooltip
                                    title={
                                        <Typography>
                                            Instant booking applies to all
                                            guests. Switch it off to apply it to
                                            selected guests only.
                                        </Typography>
                                    }
                                >
                                    <HelpOutline
                                        sx={{
                                            color: 'text.primary',
                                            fontSize: '16px',
                                        }}
                                    />
                                </CustomTooltip>
                                {!autoApprovalAppliesToAll && (
                                    <>
                                        <FormMultiAutocomplete
                                            name='autoApprovalGuests'
                                            control={control}
                                            options={
                                                guests?.map(
                                                    (guest: {
                                                        id: number;
                                                        name: string;
                                                    }) => ({
                                                        value: { id: guest.id },
                                                        label: guest.name,
                                                    }),
                                                ) || []
                                            }
                                        />
                                        <FormHelperText>
                                            Instant booking applies to selected
                                            guests only.
                                        </FormHelperText>
                                    </>
                                )}
                            </Box>
                        </Grid>
                    )}
                </Grid>

                {type === 'vehicle' && (
                    <>
                        <div>
                            <InputLabel htmlFor='minimum-licence-required__input'>
                                Minimum Licence Required
                            </InputLabel>
                            <FormSelect
                                id='minimum-licence-required__input'
                                name='details.minimumLicenceRequired'
                                control={control}
                                options={[
                                    { value: 'full', label: 'Full' },
                                    { value: 'p2', label: 'P2' },
                                    { value: 'p1', label: 'P1' },
                                ]}
                            />
                        </div>
                        <div>
                            <InputLabel htmlFor='minimum-drivers-age__input'>
                                Minimum Driver's Age (optional)
                            </InputLabel>
                            <FormAutocomplete
                                id='minimum-drivers-age__input'
                                name='details.minimumDriversAge'
                                control={control}
                                options={[...driversAgeArray]}
                            />
                        </div>
                        <div>
                            <InputLabel htmlFor='pets-allowed__input'>
                                Pets Allowed
                            </InputLabel>
                            <FormAutocomplete
                                id='pets-allowed__input'
                                name='details.petsAllowed'
                                control={control}
                                options={[
                                    { value: false, label: 'No' },
                                    { value: true, label: 'Yes' },
                                ]}
                            />
                        </div>
                        <div>
                            <InputLabel htmlFor='smoking-allowed__input'>
                                Smoking Allowed
                            </InputLabel>
                            <FormSelect
                                id='smoking-allowed__input'
                                name='details.smokingAllowed'
                                control={control}
                                options={[
                                    { value: false, label: 'No' },
                                    { value: true, label: 'Yes' },
                                ]}
                            />
                        </div>
                    </>
                )}
            </Stack>
        </fieldset>
    );
};

export default ExperienceAvailability;
